// apps/web/src/components/question/QuestionForm.tsx
// 题目编辑表单 — 支持 single_choice / multiple_choice / true_false / fill_blank

import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { QuestionType } from "@examfast/shared-types";
import toast from "react-hot-toast";
import { addQuestionApi, updateQuestionApi } from "@/api/questions";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const TYPE_LABELS: Record<QuestionType, string> = {
  single_choice: "单选题",
  multiple_choice: "多选题",
  true_false: "判断题",
  fill_blank: "填空题",
  essay: "简答题",
};

// 动态 schema — 根据题型变化
function makeSchema(type: QuestionType) {
  const base = {
    type: z.enum(Object.values(QuestionType) as [QuestionType, ...QuestionType[]]),
    content: z.string().min(1, "题干不能为空"),
    explanation: z.string().optional(),
    chapter: z.string().optional(),
    difficulty: z.number().min(1).max(5).optional(),
    tags: z.array(z.string()).optional(),
  };

  if (type === "single_choice" || type === "multiple_choice") {
    return z.object({
      ...base,
      options: z.array(z.object({ value: z.string().min(1, "选项不能为空") })).min(2, "至少 2 个选项"),
      correct_answer:
        type === "single_choice"
          ? z.string().min(1, "请选择正确答案")
          : z.array(z.string()).min(1, "至少选择 1 个正确答案"),
    });
  }

  if (type === "true_false") {
    return z.object({
      ...base,
      correct_answer: z.boolean({ required_error: "请选择正确答案" }),
    });
  }

  // fill_blank / essay
  return z.object({
    ...base,
    correct_answer: z.union([
      z.string().min(1, "请输入参考答案"),
      z.array(z.string().min(1)).min(1),
    ]),
  });
}

type OptionItem = { value: string };

type FormValues = {
  type: QuestionType;
  content: string;
  options: OptionItem[];
  correct_answer: string | string[] | boolean;
  explanation?: string;
  chapter?: string;
  difficulty?: number;
  tags?: string[];
};

interface Props {
  defaultValues?: Partial<FormValues> & { qid?: string };
  bankId: string;
  onSuccess?: () => void;
}

export default function QuestionForm({ defaultValues, bankId, onSuccess }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isEdit = !!defaultValues?.qid;
  const type = defaultValues?.type ?? "single_choice";

  // 编辑模式：后端返回 options 是 string[]，需要转成 OptionItem[]
  const normalizedDefaults: Partial<FormValues> & { qid?: string } = {
    ...defaultValues,
    options:
      defaultValues?.options?.map((o) =>
        typeof o === "string" ? { value: o } : (o as OptionItem)
      ) ??
      (type === "single_choice" || type === "multiple_choice"
        ? [{ value: "" }, { value: "" }]
        : []),
  };

  const schema = makeSchema(type);

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<FormValues>({
    resolver: zodResolver(schema as z.ZodTypeAny),
    defaultValues: {
      type,
      content: "",
      options: [{ value: "" }, { value: "" }],
      correct_answer: type === "multiple_choice" ? [] : "",
      ...normalizedDefaults,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "options",
  });

  const watchType = watch("type");
  const watchCorrect = watch("correct_answer");

  const mutation = useMutation({
    mutationFn: async (vals: FormValues) => {
      const payload = {
        ...vals,
        options:
          vals.type === "single_choice" || vals.type === "multiple_choice"
            ? vals.options.map((o) => o.value).filter(Boolean)
            : undefined,
      };
      if (isEdit && defaultValues?.qid) {
        return updateQuestionApi(bankId, defaultValues.qid, payload);
      }
      return addQuestionApi(bankId, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["questions", bankId] });
      toast.success(isEdit ? "题目已更新" : "题目已添加");
      onSuccess?.();
      navigate(`/banks/${bankId}`);
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "保存失败");
    },
  });

  return (
    <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-5" noValidate>
      {/* 题型选择 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">题型</label>
        <select
          {...register("type")}
          className="w-full px-3 py-2 border border-gray-200 rounded-xl bg-gray-50/50 text-sm"
          onChange={(e) => {
            const t = e.target.value as QuestionType;
            setValue("type", t);
            if (t === "single_choice" || t === "multiple_choice") {
              setValue("options", [{ value: "" }, { value: "" }]);
              setValue("correct_answer", t === "multiple_choice" ? [] : "");
            } else if (t === "true_false") {
              setValue("options", []);
              setValue("correct_answer", true);
            } else {
              setValue("options", []);
              setValue("correct_answer", "");
            }
          }}
        >
          {Object.entries(TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* 题干 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">题干</label>
        <textarea
          {...register("content")}
          rows={3}
          className="w-full px-3 py-2 border border-gray-200 rounded-xl bg-gray-50/50 text-sm"
          placeholder="请输入题干内容"
        />
        {errors.content && <p className="text-danger text-xs mt-1">{errors.content.message}</p>}
      </div>

      {/* 选项（单选/多选） */}
      {(watchType === "single_choice" || watchType === "multiple_choice") && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            选项（{(watchType === "single_choice" ? "单选" : "多选")}）
          </label>
          <div className="space-y-2">
            {fields.map((field, idx) => (
              <div key={field.id} className="flex items-center gap-2">
                <span className="text-sm text-gray-500 w-6">{String.fromCharCode(65 + idx)}.</span>
                <input
                  {...register(`options.${idx}.value` as const)}
                  defaultValue={field.value}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  placeholder={`选项 ${String.fromCharCode(65 + idx)}`}
                />
                {fields.length > 2 && (
                  <button type="button" onClick={() => remove(idx)} className="text-danger text-sm px-2">
                    删除
                  </button>
                )}
                {/* 选择正确答案 */}
                {watchType === "single_choice" ? (
                  <input
                    type="radio"
                    name="correct_single"
                    aria-label={`将选项 ${String.fromCharCode(65 + idx)} 设为正确答案`}
                    checked={watchCorrect === String.fromCharCode(65 + idx)}
                    onChange={() => setValue("correct_answer", String.fromCharCode(65 + idx))}
                  />
                ) : (
                  <input
                    type="checkbox"
                    aria-label={`将选项 ${String.fromCharCode(65 + idx)} 加入正确答案`}
                    checked={(watchCorrect as string[] ?? []).includes(String.fromCharCode(65 + idx))}
                    onChange={(e) => {
                      const val = String.fromCharCode(65 + idx);
                      const curr = [...(watchCorrect as string[] ?? [])];
                      if (e.target.checked) curr.push(val);
                      else curr.splice(curr.indexOf(val), 1);
                      setValue("correct_answer", curr);
                    }}
                  />
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => append({ value: "" })}
            className="mt-2 text-sm text-primary"
          >
            + 添加选项
          </button>
          {errors.options && <p className="text-danger text-xs mt-1">{errors.options.message}</p>}
          {errors.correct_answer && <p className="text-danger text-xs mt-1">请选择正确答案</p>}
        </div>
      )}

      {/* 判断题正确答案 */}
      {watchType === "true_false" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">正确答案</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-1">
              <input
                type="radio"
                value="true"
                checked={watchCorrect === true}
                onChange={() => setValue("correct_answer", true)}
              />
              正确
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                value="false"
                checked={watchCorrect === false}
                onChange={() => setValue("correct_answer", false)}
              />
              错误
            </label>
          </div>
        </div>
      )}

      {/* 填空题 / 简答题参考答案 */}
      {(watchType === "fill_blank" || watchType === "essay") && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {watchType === "fill_blank" ? "参考答案（多个答案用换行分隔）" : "参考答案"}
          </label>
          <textarea
            {...register("correct_answer")}
            rows={watchType === "essay" ? 5 : 3}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl bg-gray-50/50 text-sm"
            placeholder={watchType === "fill_blank" ? "每个答案一行" : "请输入参考答案"}
          />
        </div>
      )}

      {/* 解析 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">解析（可选）</label>
        <textarea
          {...register("explanation")}
          rows={2}
          className="w-full px-3 py-2 border border-gray-200 rounded-xl bg-gray-50/50 text-sm"
          placeholder="题目解析（选填）"
        />
      </div>

      {/* 章节 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">章节（可选）</label>
        <input
          {...register("chapter")}
          className="w-full px-3 py-2 border border-gray-200 rounded-xl bg-gray-50/50 text-sm"
          placeholder="如：第一章"
        />
      </div>

      {/* 难度 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">难度（1-5，可选）</label>
        <input
          type="number"
          min={1}
          max={5}
          {...register("difficulty", {
            setValueAs: (v) => (v === "" || v == null ? undefined : Number(v)),
          })}
          className="w-24 px-3 py-2 border border-gray-200 rounded-xl bg-gray-50/50 text-sm"
        />
      </div>

      {/* 提交 */}
      <button
        type="submit"
        disabled={isSubmitting || mutation.isPending}
        className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl btn-tap disabled:opacity-50"
      >
        {mutation.isPending ? "保存中..." : isEdit ? "更新题目" : "添加题目"}
      </button>
      {mutation.isError && (
        <p className="text-danger text-sm text-center">{(mutation.error as Error).message}</p>
      )}
    </form>
  );
}
